// components/blog/BlogInfiniteGrid.client.tsx
'use client';

import DotLoader, { DotEllipsis } from '@/components/ui/DotLoader';
import { HTTP_REQUEST } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { fetcher } from '@/lib/Requester';
import type { Blog, BlogsResponse } from '@/models/BlogsResponse';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import SmartImage from './SmartImage';

/* ---------- helpers ---------- */

// Stable key for de-duplication
const keyOf = (b: Blog) => String(b.url ?? b.id ?? b.title);

// Merge without duplicates (by url/id/title)
function uniqueMerge(prev: Blog[], next: Blog[]) {
  const seen = new Set<string>();
  const out: Blog[] = [];
  for (const x of prev) {
    const k = keyOf(x);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(x);
    }
  }
  for (const x of next) {
    const k = keyOf(x);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(x);
    }
  }
  return out;
}

// Minimal card; replace with your real BlogItem if you have one
function BlogItem({ blog }: { blog: Blog }) {
  // Pick first <img src="..."> from HTML as thumbnail (optional)
  const cleaned = blog.content?.replace(/\\"/g, '"').replace(/\\'/g, "'");
  const m = cleaned?.match(/<img[^>]+src=["']?([^"'>\s]+)["']?[^>]*>/i);
  const img = m?.[1];

  return (
    <Link
      href={`/${blog.url}`}
      className="group cursor-pointer w-full border border-gray-300 rounded-2xl p-4 transition-all duration-300 hover:border-primary"
    >
      {/* Fixed aspect ratio + cover for list view */}
      <div className="mb-6">
        <div className="relative w-full overflow-hidden rounded-lg aspect-[16/9]">
          {img ? (
            <SmartImage
              src={img}
              alt={blog.title}
              width={0}
              height={0}
              sizes="(max-width:768px) 100vw, (max-width:1024px) 50vw, 33vw"
              className="block w-full h-full object-cover"
              quality={50}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full bg-gray-100" />
          )}
        </div>
      </div>

      <h4 className="text-gray-900 text-sm font-medium mb-2 line-clamp-2">{blog.title}</h4>
      <span className="text-gray-700 text-xs font-normal mb-4 line-clamp-3">
        {blog.description}
      </span>
      <div className="font-normal text-sm bg-hzc_beige_400 px-6 py-2 rounded-md w-fit group-hover:scale-105 transition-transform">
        DEVAMINI OKU
      </div>
    </Link>
  );
}

/* ---------- main ---------- */

export default function BlogInfiniteGrid({
  initialBlogs,
  initialPage, // 1-based
  rowsPerPage, // e.g. 12
}: {
  initialBlogs: Blog[];
  initialPage: number;
  rowsPerPage: number;
}) {
  /** Keep SSR and CSR in sync to avoid hydration issues */
  const [blogs, setBlogs] = useState<Blog[]>(initialBlogs);
  const [page, setPage] = useState<number>(initialPage);

  /** Loading + done flags with refs to avoid race conditions */
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const setLoadingSafe = (v: boolean) => {
    loadingRef.current = v;
    setLoading(v);
  };

  // If backend doesn't give total, we finish when a fetched page returns < rowsPerPage.
  // If initial page is empty, allow loading to proceed (not done).
  const [done, setDone] = useState<boolean>(
    initialBlogs.length > 0 ? initialBlogs.length < rowsPerPage : false,
  );
  const doneRef = useRef(done);
  useEffect(() => {
    doneRef.current = done;
  }, [done]);

  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Cache-buster to avoid intermediate caches returning stale pages
  const ts = () => Date.now();

  // Track merged length for total-based finish checks
  const mergedLenRef = useRef<number>(initialBlogs.length);

  // Simple retry control
  const retryRef = useRef(0);
  const MAX_RETRY = 2;

  /** Load the next page (single-flight) */
  const loadNext = useCallback(async () => {
    if (loadingRef.current || doneRef.current) return;
    setLoadingSafe(true);

    const nextPage = pageRef.current + 1;

    try {
      const res = await fetcher<BlogsResponse>({
        endpoint: Endpoints.BLOG_LIST,
        method: HTTP_REQUEST.GET,
        // Your model: res.blogs.records (page items), res.blogs.rowCount (TOTAL)
        params: { page: nextPage, rowsPerPage },
        cacheEnabled: false,
      });

      const nextRecords: Blog[] = res?.blogs?.records ?? [];
      const totalCount: number | undefined =
        typeof res?.blogs?.rowCount === 'number' ? res.blogs.rowCount : undefined;

      // Merge; record merged length
      setBlogs((prev) => {
        const merged = uniqueMerge(prev, nextRecords);
        mergedLenRef.current = merged.length;
        return merged;
      });

      // Decide "done"
      if (totalCount !== undefined) {
        if (mergedLenRef.current >= totalCount) setDone(true);
      } else {
        if (nextRecords.length < rowsPerPage) setDone(true);
      }

      setPage(nextPage);
      retryRef.current = 0; // reset on success
    } catch (err) {
      // Retry a couple of times; keep UX simple (dots keep animating)
      if (retryRef.current < MAX_RETRY && !doneRef.current) {
        retryRef.current += 1;
        setTimeout(() => loadNext(), 800);
      } else {
        // Give up for now; user scroll/resize will re-kick via fallback
        // eslint-disable-next-line no-console
        console.error('[blogs] loadNext error:', err);
      }
    } finally {
      setLoadingSafe(false);

      // Chain if sentinel is still visible (prevents getting stuck after a short append)
      const el = sentinelRef.current;
      if (el && !doneRef.current && !loadingRef.current) {
        const margin = 800; // keep in sync with IO rootMargin
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        if (r.top <= vh + margin) queueMicrotask(() => loadNext());
      }
    }
  }, [rowsPerPage]);

  /** IntersectionObserver: auto-load when footer enters viewport */
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Kick-off fallback (if IO doesn't fire due to layout/short page/scroll container)
  const kickIfNeeded = useCallback(() => {
    if (doneRef.current || loadingRef.current) return;
    const el = sentinelRef.current;
    if (!el) return;
    const margin = 1000;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.top <= vh + margin) loadNext();
  }, [loadNext]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || doneRef.current) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          queueMicrotask(() => loadNext());
        }
      },
      { root: null, rootMargin: '1000px 0px 1000px 0px', threshold: 0 },
    );
    io.observe(el);

    // Fallbacks: run once after paint; also on scroll/resize
    const t = window.setTimeout(kickIfNeeded, 0);
    const onScroll = () => kickIfNeeded();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      io.disconnect();
      window.clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [loadNext, kickIfNeeded]);

  return (
    <>
      {/* Grid */}
      <div className="grid lg:grid-cols-3 max-w-7xl md:grid-cols-2 grid-cols-1 gap-4 w-full h-full">
        {blogs.map((b) => (
          <BlogItem key={keyOf(b)} blog={b} />
        ))}
      </div>

      {/* Footer: animated dots while loading; static dots when finished */}
      <div ref={sentinelRef} className="w-full flex items-center justify-center py-8">
        {done ? (
          <DotEllipsis className="text-2xl md:text-3xl text-neutral-400" />
        ) : loading ? (
          <DotLoader className="text-gray-600 dark:text-gray-300" />
        ) : (
          <div className="h-4" /> // preserve layout when idle
        )}
      </div>
    </>
  );
}
